import {
  Box,
  Button,
  ButtonProps,
  HStack,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Spinner,
  useColorModeValue,
  useStyleConfig,
} from "@chakra-ui/react";
import { RiArrowDownSLine } from "@remixicon/react";
import { useMemo } from "react";
import { Link } from "react-router";

import { useFeatureViewsData } from "@/app/data/useFeatureViewsData";
import { HeaderLayoutHeading } from "@/common/components/HeaderLayout";
import {
  IcoArchive,
  IcoFeature,
  IcoFeatureCleanup,
  IcoFeatureView,
  IcoUser,
} from "@/common/components/Icons";
import { NavLink } from "@/common/components/NavLink";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useFlag } from "@reflag/react-sdk";
import { GlobalSettingsUrl } from "@reflag/shared/urls";

import { useSelectedFeatureView } from "../hooks/useSelectedFeatureView";
import { getFlagViewDisplayName } from "../utils/flagView";

type FeatureViewSelectProps = Omit<ButtonProps, "size"> & {
  size?: "sm" | "md";
};

const sizeMap = {
  sm: {
    arrowIconSize: "md",
    menuButtonSize: "sm",
    headingSize: "sm",
  },
  md: {
    arrowIconSize: "lg",
    menuButtonSize: "sm",
    headingSize: "lg",
  },
};

export function FeatureViewSelect({
  size = "md",
  ...rest
}: FeatureViewSelectProps) {
  const { env } = useEnvironment();
  const [view] = useSelectedFeatureView();

  const sizes = sizeMap[size];

  const { data: featureViews = [], isLoading: isLoadingFeatureViews } =
    useFeatureViewsData();

  const { isEnabled: isArchivingEnabled } = useFlag("archiving");

  const [systemMenuItems, customMenuItems, currentMenuItem] = useMemo(() => {
    const allView = featureViews.find((view) => view.type === "all");
    const myView = featureViews.find((view) => view.type === "my");
    const archivedView = featureViews.find((view) => view.type === "archived");
    const staleView = featureViews.find((view) => view.type === "stale");
    const customViews = featureViews.filter((view) => view.type === "custom");

    const allMenuItem = allView && {
      id: allView.id,
      to: `?view=${allView.id}`,
      label: getFlagViewDisplayName(allView),
      icon: <IcoFeature size="md" />,
    };

    const myMenuItem = myView
      ? {
          id: myView.id,
          to: `?view=${myView.id}`,
          label: myView.name,
          icon: <IcoUser size="md" />,
        }
      : null;

    const archivedMenuItem = archivedView && {
      id: archivedView.id,
      to: `?view=${archivedView.id}`,
      label: archivedView.name,
      icon: <IcoArchive size="md" />,
    };

    const staleMenuItem = staleView && {
      id: staleView.id,
      to: `?view=${staleView.id}`,
      label: staleView.name,
      icon: <IcoFeatureCleanup size="md" />,
    };

    const customMenuItems = customViews
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((view) => ({
        id: view.id,
        to: `?view=${view.id}`,
        label: view.name,
        icon: <IcoFeatureView size="md" />,
      }));

    const systemMenuItems = [allMenuItem, myMenuItem];
    if (isArchivingEnabled) {
      systemMenuItems.push(staleMenuItem, archivedMenuItem);
    }

    const currentMenuItem = [...systemMenuItems, ...customMenuItems].find(
      (i) => i?.id === view?.id,
    );

    return [
      systemMenuItems.filter(Boolean) as NonNullable<typeof allMenuItem>[],
      customMenuItems,
      currentMenuItem,
    ];
  }, [featureViews, isArchivingEnabled, view]);

  const currentItemColor = useColorModeValue("brand.500", "brand.300");

  return (
    <Menu>
      <MenuButton
        aria-label="Switch view"
        as={Button}
        display="flex"
        pl={2}
        pr={1}
        rightIcon={
          <Box fontSize={sizes.arrowIconSize} ml={-0.5}>
            <RiArrowDownSLine size={16} />
          </Box>
        }
        size={sizes.menuButtonSize}
        variant="outlineOnHover"
        {...rest}
      >
        <HStack spacing={1.5}>
          {currentMenuItem?.icon}
          <HeaderLayoutHeading fontSize={sizes.headingSize}>
            {currentMenuItem?.label}
          </HeaderLayoutHeading>
        </HStack>
      </MenuButton>
      <MenuList>
        <MenuDivider my={0} />
        {isLoadingFeatureViews ? (
          <Box pb={1} pt={2} px={3}>
            <Spinner size="xs" variant="subtle" />
          </Box>
        ) : (
          <>
            {systemMenuItems.map((i) => (
              <MenuItem
                key={i.id}
                as={NavLink}
                icon={i.icon}
                matchSearchParams={["view"]}
                sx={{
                  "&[aria-current='page']": {
                    color: currentItemColor,
                  },
                }}
                to={i.to}
              >
                {i.label}
              </MenuItem>
            ))}
            {customMenuItems.length > 0 && <MenuDivider my={2} />}
            {customMenuItems.map((i, index) => (
              <MenuItem
                key={i.id}
                as={NavLink}
                icon={i.icon}
                matchSearchParams={["view"]}
                mb={index === customMenuItems.length - 1 ? 2 : 0}
                sx={{
                  "&[aria-current='page']": {
                    color: currentItemColor,
                  },
                }}
                to={i.to}
              >
                {i.label}
              </MenuItem>
            ))}
          </>
        )}

        <MenuItem
          as={Link}
          display="block"
          m="2"
          sx={useStyleConfig("Button", {
            variant: "outline",
            size: "xs",
          })}
          to={GlobalSettingsUrl(env, "app-feature-views")}
          width="max-content"
        >
          Manage views
        </MenuItem>
      </MenuList>
    </Menu>
  );
}
